import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Create a GST Invoice in India (2025 Guide)',
      'description': 'Step-by-step guide to creating GST-compliant tax invoices in India. Covers mandatory fields, CGST/SGST/IGST, and common mistakes to avoid.',
      'url': 'https://www.invilic.com/blog/how-to-create-gst-invoice-india',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Who is required to issue a GST invoice in India?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Every GST-registered supplier must issue a tax invoice for taxable supplies of goods or services. Registration is compulsory when annual aggregate turnover exceeds ₹40 lakh for goods (₹20 lakh for services; ₹10 lakh in northeastern states). Composition scheme dealers issue a Bill of Supply instead of a Tax Invoice.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the time limit to issue a GST invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'For goods, a tax invoice must be issued at the time of removal or delivery, whichever is earlier. For services, it must be issued within 30 days of the date of supply. For banking and financial institutions, the limit extends to 45 days.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between CGST, SGST, and IGST?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'CGST (Central GST) and SGST (State GST) are charged on intra-state supplies — when buyer and seller are in the same state. Each is levied at half the applicable GST rate. IGST (Integrated GST) is charged on inter-state supplies — when buyer and seller are in different states — at the full GST rate. Exports are zero-rated under IGST.' },
        },
        {
          '@type': 'Question',
          'name': 'Can I create a GST invoice if I am not GST registered?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'No. You cannot issue a GST Tax Invoice if you are not registered under GST. Unregistered businesses issue regular invoices without any GST charge. Once your turnover exceeds the threshold, you must register and start issuing compliant tax invoices.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

export default function HowToCreateGstInvoiceIndiaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Create a GST Invoice in India (2025 Guide) | Invilic Blog"
        description="Step-by-step guide to creating a GST-compliant tax invoice in India. Covers mandatory fields, CGST/SGST/IGST, HSN codes, and common mistakes."
        canonical="https://www.invilic.com/blog/how-to-create-gst-invoice-india"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">India GST</span>
          </div>
          <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">India GST</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Create a GST Invoice in India (2025 Guide)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            A GST invoice in India must include your GSTIN, the buyer's GSTIN, a separate CGST/SGST or IGST breakdown, and an HSN or SAC code for each line item. Every GST-registered supplier is legally required to issue a tax invoice for each taxable supply — and missing even one mandatory field can invalidate the document.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is a GST Invoice?</h2>
          <p className="mb-4">
            A GST invoice (formally called a Tax Invoice) is the document a registered supplier issues when selling taxable goods or services. It serves two purposes: it creates your tax liability with the government, and it gives the buyer proof of GST paid so they can claim an Input Tax Credit (ITC) on that amount.
          </p>
          <p className="mb-4">
            Without a valid GST invoice, your buyer cannot claim ITC — which means they effectively pay more tax. This is why buyers insist on correct invoices, and why the GST law is specific about what each invoice must contain.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Mandatory Fields on a GST Invoice</h2>
          <p className="mb-4">Under the CGST Rules 2017, a tax invoice must contain all of the following:</p>
          <ul className="space-y-2 mb-6 pl-1">
            {[
              'Name, address, and GSTIN of the supplier',
              'A consecutive serial number (unique per financial year)',
              'Date of issue',
              'Name, address, and GSTIN of the recipient (if registered)',
              'Description of goods or services',
              'HSN code (for goods) or SAC code (for services)',
              'Quantity and unit of measurement (for goods)',
              'Total taxable value after any discounts',
              'Applicable GST rate and amount — CGST + SGST for intra-state, or IGST for inter-state',
              'Place of supply (state name and code)',
              'Whether the supply is taxable under reverse charge',
              'Signature or digital signature of the supplier',
            ].map(item => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <BlogCTA
            text="Need to create a GST invoice right now? Invilic generates CGST/SGST/IGST invoices in seconds — free, no sign-up needed."
            to="/create/gst"
            btnText="Create GST Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Step-by-Step: Creating a GST Invoice</h2>

          <div className="space-y-6 mb-6">
            {[
              { step: '1', title: 'Enter your business details', body: 'Add your legal business name, full address, and your GSTIN. If you have a business logo, upload it — it makes the invoice look professional and helps with your brand.' },
              { step: '2', title: 'Add your buyer\'s details', body: 'Enter the buyer\'s name, address, and their GSTIN (if they are GST-registered). The buyer\'s GSTIN is critical if they want to claim ITC. For unregistered buyers, leave the GSTIN blank.' },
              { step: '3', title: 'Set the place of supply', body: 'Select the state where the supply is being made. If your state and the buyer\'s state are the same, CGST + SGST apply. If they are different, IGST applies. This determines which tax appears on the invoice.' },
              { step: '4', title: 'Add line items with HSN/SAC codes', body: 'For each product or service, enter the HSN code (goods) or SAC code (services), description, quantity, and unit price. The HSN code determines which GST rate applies.' },
              { step: '5', title: 'Select the GST rate', body: 'Choose the applicable GST slab: 0%, 5%, 12%, 18%, or 28%. The system splits this into CGST + SGST (intra-state) or shows it as IGST (inter-state) based on your place of supply selection.' },
              { step: '6', title: 'Review totals and download', body: 'Verify the subtotal, GST amount, and grand total. Add your bank details, payment terms, and any notes. Then download your PDF.' },
            ].map(({ step, title, body }) => (
              <div key={step} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0 text-green-700 dark:text-green-300 font-bold text-sm">
                  {step}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-1">{title}</p>
                  <p className="text-sm">{body}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Tax Invoice vs Bill of Supply: Which Do You Need?</h2>
          <p className="mb-4">
            A <strong className="font-semibold text-gray-900 dark:text-white">Tax Invoice</strong> is used for all taxable supplies. A <strong className="font-semibold text-gray-900 dark:text-white">Bill of Supply</strong> is used when GST does not apply — specifically for exempt goods/services, or by suppliers registered under the Composition Scheme.
          </p>
          <p className="mb-6">
            If you are a composition dealer, you cannot charge GST from your buyers. You pay a fixed composition tax on your turnover and issue a Bill of Supply instead. The Bill of Supply must state "Composition taxable person, not eligible to collect tax on supplies."
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Common GST Invoicing Mistakes to Avoid</h2>
          <ul className="space-y-3 mb-6">
            {[
              { mistake: 'Wrong GSTIN on the invoice', fix: 'Double-check both your own and your buyer\'s GSTIN before issuing. An incorrect GSTIN means the buyer cannot claim ITC.' },
              { mistake: 'Missing HSN/SAC codes', fix: 'Businesses with turnover above ₹5 crore must include 6-digit HSN codes. Below ₹5 crore, 4-digit codes are sufficient.' },
              { mistake: 'Incorrect state code for IGST/CGST-SGST split', fix: 'Always confirm whether the supply is intra-state or inter-state. Getting this wrong affects how GST is distributed between Central and State governments.' },
              { mistake: 'No invoice serial number', fix: 'Invoice numbers must be consecutive within a financial year. Gaps in the sequence can raise questions during a GST audit.' },
            ].map(({ mistake, fix }) => (
              <li key={mistake} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4">
                <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">✗ {mistake}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Fix: {fix}</p>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a GST-compliant tax invoice with CGST, SGST, and IGST auto-calculated. Free — no account required."
            to="/create/gst"
            btnText="Create GST Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/how-to-create-gst-invoice-india" />
      </main>
    </div>
  );
}
